import React, { useCallback, useEffect, useState } from 'react';
import { CaretLeft } from '@phosphor-icons/react';
import { Card, Button } from '../ui';
import { TRAINING_SOURCES } from '../../config/trainingSources';
import { fetchGarminStatus } from '../../utils/garmin';
import { requestTrainingConnectionsScroll } from '../TrainingConnectionsSection';
import { ProviderLogo } from '../connections/ConnectionParts';

// Home Garmin connection status (Strava removal C2, 2026-10-21). The source of truth is the same /garmin/status the
// Profile "חיבורי אימונים" card reads (GarminConnectCard), mapped the same way. The connect / reconnect / manage flow
// itself stays in Profile — the button leads there. A failed status request is NEVER shown as "not connected".
//   loading       neutral dot  "בודק חיבור..."
//   connected     green dot    "Garmin מחובר"
//   disconnected  red dot      "Garmin לא מחובר"          + connect action
//   attention     red dot      "Garmin דורש חיבור מחדש"   + reconnect action (reauth / sync error, as in Profile)
//   pending       neutral dot  "החיבור ממתין לאישור"
//   error         neutral dot  "לא ניתן לבדוק את החיבור כרגע" + retry (request failed — unknown, not disconnected)
// Not eligible (the backend does not offer Garmin to this user) or Garmin off -> the card is not shown.

export function garminHomeState(s) {
  if (!s || !s.eligible) return 'hidden';
  if (s.status === 'reauth_required' || (s.status === 'connected' && s.error)) return 'attention';
  if (s.status === 'connected') return 'connected';
  if (s.status === 'pending') return 'pending';
  return 'disconnected';
}

const VIEW = {
  loading: { dot: 'bg-text-muted opacity-50', text: 'text-text-muted', label: 'בודק חיבור...' },
  connected: { dot: 'bg-positive', text: 'text-positive', label: 'Garmin מחובר', action: 'ניהול חיבור', primary: false },
  disconnected: { dot: 'bg-quality', text: 'text-quality', label: 'Garmin לא מחובר', action: 'חיבור Garmin', primary: true },
  attention: { dot: 'bg-quality', text: 'text-quality', label: 'Garmin דורש חיבור מחדש', action: 'חיבור מחדש', primary: true },
  pending: { dot: 'bg-text-muted', text: 'text-text-muted', label: 'החיבור ממתין לאישור', action: 'ניהול חיבור', primary: false },
  error: { dot: 'border border-text-muted bg-transparent', text: 'text-text-muted', label: 'לא ניתן לבדוק את החיבור כרגע' },
};

export default function TrainingSourcesCard({ onManage }) {
  const garminSource = TRAINING_SOURCES.find((s) => s.id === 'garmin');
  const enabled = !!(garminSource && garminSource.enabled);
  const [state, setState] = useState(enabled ? 'loading' : 'hidden');

  const load = useCallback(() => {
    let alive = true;
    setState('loading');
    fetchGarminStatus()
      .then((s) => { if (alive) setState(garminHomeState(s)); })
      .catch(() => { if (alive) setState('error'); });
    return () => { alive = false; };
  }, []);

  useEffect(() => (enabled ? load() : undefined), [enabled, load]);

  if (state === 'hidden') return null;
  const v = VIEW[state];
  const go = () => { requestTrainingConnectionsScroll(); onManage(); };

  return (
    <Card className="mb-4 flex flex-col" data-testid="training-sources-card">
      <h3 className="font-bold text-[17px] text-text">מקור האימונים שלי</h3>
      <div className="mt-2 flex items-center gap-3 py-2" data-testid="garmin-home-status" data-state={state}>
        <ProviderLogo source={garminSource} size={36} />
        <span className={`inline-flex items-center gap-2 font-semibold text-[15px] ${v.text}`} role="status" aria-live="polite">
          <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${v.dot}`} aria-hidden="true" />
          {v.label}
        </span>
      </div>
      {state === 'error' && (
        <Button variant="secondary" className="mt-2 h-12" onClick={load}>נסה שוב</Button>
      )}
      {v.action && (
        <Button
          variant={v.primary ? 'primary' : 'secondary'}
          className="mt-2 h-12"
          trailingIcon={<CaretLeft size={16} weight="bold" />}
          onClick={go}
        >
          {v.action}
        </Button>
      )}
    </Card>
  );
}
